import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"

export default class extends Controller {
  connect() {
    this.fetchDataAndRender()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
  }

  async fetchDataAndRender() {
    try {
      // Get playlist details from parent row
      const playlistRow = this.element.closest('tr').previousElementSibling
      const playlistUri = playlistRow.dataset.playlistUri
      const dspSite = playlistRow.dataset.dspSite

      // Get current URL parameters
      const urlParams = new URLSearchParams(window.location.search)

      // Build the fetch URL with the same parameters
      const params = new URLSearchParams()
      params.append('playlist_uri', playlistUri)
      params.append('dsp_site', dspSite)

      // Add date and territory filters
      if (urlParams.get('start_date')) params.append('start_date', urlParams.get('start_date'))
      if (urlParams.get('end_date')) params.append('end_date', urlParams.get('end_date'))
      if (urlParams.get('date_preset')) params.append('date_preset', urlParams.get('date_preset'))
      urlParams.getAll('territories[]').forEach(t => params.append('territories[]', t))

      const fetchUrl = `/trends/playlist_streams_by_day?${params.toString()}`

      const response = await fetch(fetchUrl)
      const data = await response.json()

      this.renderChart(data)
    } catch (error) {
      console.error('Error fetching playlist streams chart data:', error)
      this.showError()
    }
  }

  renderChart(data) {
    if (!data || !data.series || data.series.length === 0) {
      this.showEmptyState()
      return
    }

    // Clear the loading message and any existing content
    this.element.innerHTML = ''

    const options = {
      series: data.series,
      chart: {
        type: 'line',
        height: 400,
        toolbar: {
          show: false
        },
        zoom: {
          enabled: false
        }
      },
      stroke: {
        curve: 'smooth',
        width: 2
      },
      xaxis: {
        type: 'datetime',
        labels: {
          format: 'MMM dd'
        }
      },
      yaxis: {
        labels: {
          formatter: function(value) {
            return Math.floor(value).toLocaleString()
          }
        }
      },
      tooltip: {
        shared: true,
        intersect: false,
        custom: function({series, seriesIndex, dataPointIndex, w}) {
          const dateTimestamp = w.globals.seriesX[seriesIndex][dataPointIndex]
          const date = new Date(dateTimestamp)

          const formattedDate = date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC'
          })

          let tooltipHtml = '<div class="px-3 py-2" style="min-width: 200px;">'
          tooltipHtml += `<div class="font-semibold text-gray-900 mb-2">${formattedDate}</div>`

          const value = series[seriesIndex][dataPointIndex]
          const color = w.globals.colors[seriesIndex]
          const name = w.globals.seriesNames[seriesIndex]

          tooltipHtml += '<div class="flex items-center justify-between gap-4 py-1">'
          tooltipHtml += `<div class="flex items-center gap-2">`
          tooltipHtml += `<div class="w-3 h-3 rounded-sm" style="background-color: ${color};"></div>`
          tooltipHtml += `<span class="text-sm text-gray-700">${name}</span>`
          tooltipHtml += `</div>`
          tooltipHtml += `<span class="text-sm font-medium text-gray-900">${value.toLocaleString()} streams</span>`
          tooltipHtml += '</div>'

          tooltipHtml += '</div>'
          return tooltipHtml
        }
      },
      legend: {
        show: false
      },
      colors: data.colors || ['#19B26C'],
      grid: {
        borderColor: '#e7e7e7',
        row: {
          colors: ['#f3f3f3', 'transparent'],
          opacity: 0.5
        }
      }
    }

    this.chart = new ApexCharts(this.element, options)
    this.chart.render()
  }

  showEmptyState() {
    this.element.innerHTML = '<div class="text-center py-8 text-gray-500">No playlist streams data available for the selected filters.</div>'
  }

  showError() {
    this.element.innerHTML = '<div class="text-center py-8 text-red-500">Error loading chart data. Please try again.</div>'
  }
}
